/**
 * NotificationItem Component
 *
 * Individual notification row with icon, content, timestamp, and dismiss button.
 * Supports i18n through metadata.i18n translation parameters.
 */

import { useTranslation } from 'react-i18next';
import { HEALTH_LABEL_KEY } from '@/components/dashboard/health-severity';
import type { ProjectHealth } from '@/components/projects/ProjectHealthBadge';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/utils';

// Translation params stored in notification metadata
// Index signature required for i18next t() function compatibility
interface NotificationI18nParams {
  [key: string]: string | undefined;
  issueTitle?: string;
  actorName?: string;
  projectName?: string;
  testPlanName?: string;
  testRunVersion?: string;
  oldStatus?: string;
  newStatus?: string;
  updatedFields?: string;
  from?: string;
  to?: string;
}

// Notification metadata structure
interface NotificationMetadata {
  i18n?: NotificationI18nParams;
}

// Notification types enum
type NotificationType =
  | 'ISSUE_STATUS_CHANGED'
  | 'ISSUE_ASSIGNED'
  | 'ISSUE_COMMENTED'
  | 'ISSUE_UPDATED'
  | 'PROJECT_UPDATE_MENTION'
  | 'TEST_RUN_ASSIGNED'
  | 'RUNBOOK_STARTED'
  | 'RUNBOOK_COMPLETED'
  | 'RUNBOOK_ROLLED_BACK'
  | 'ACTION_DUE'
  | 'ACTION_OVERDUE'
  | 'ACTION_ASSIGNED'
  | 'ACTION_FAILED'
  | 'CRITICAL_ACTION_FAILED'
  | 'PROJECT_HEALTH_CHANGED';

// Types
interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  issueId: string | null;
  metadata?: NotificationMetadata;
  issue: {
    id: string;
    title: string;
    project: {
      id: string;
      name: string;
    };
  } | null;
  actor: {
    id: string;
    name: string;
    profilePictureUrl: string | null;
  } | null;
}

interface NotificationItemProps {
  notification: Notification;
  onClick: () => void;
  onDismiss: () => void;
}

// Get icon based on notification type
function getNotificationIcon(type: NotificationType): { name: string; color: string } {
  switch (type) {
    case 'ISSUE_STATUS_CHANGED':
      return { name: 'sync', color: 'text-blue-600' };
    case 'ISSUE_ASSIGNED':
      return { name: 'person_add', color: 'text-green-600' };
    case 'ISSUE_COMMENTED':
      return { name: 'chat', color: 'text-purple-600' };
    case 'PROJECT_UPDATE_MENTION':
      return { name: 'alternate_email', color: 'text-orange-600' };
    case 'TEST_RUN_ASSIGNED':
      return { name: 'science', color: 'text-teal-600' };
    case 'RUNBOOK_STARTED':
    case 'RUNBOOK_COMPLETED':
    case 'RUNBOOK_ROLLED_BACK':
      return { name: 'menu_book', color: 'text-indigo-600' };
    case 'ACTION_DUE':
    case 'ACTION_OVERDUE':
    case 'ACTION_ASSIGNED':
    case 'ACTION_FAILED':
    case 'CRITICAL_ACTION_FAILED':
      return { name: 'task_alt', color: 'text-red-600' };
    case 'PROJECT_HEALTH_CHANGED':
      return { name: 'monitor_heart', color: 'text-amber-600' };
    default:
      return { name: 'notifications', color: 'text-neutral-600' };
  }
}

export function NotificationItem({ notification, onClick, onDismiss }: NotificationItemProps) {
  const { t, i18n } = useTranslation('common');
  const { t: tIssues } = useTranslation('issues');
  const { t: tProjects } = useTranslation('projects');
  const icon = getNotificationIcon(notification.type);

  /**
   * A stored health verdict as the word the reader knows it by. The enum value itself is
   * not a sentence anybody should read.
   *
   * Reads the dashboard's table rather than declaring one: that module owns the mapping
   * and a test there pins it to a single declaration. A verdict the table does not know
   * falls back to its raw value, which is the same choice the dashboard surfaces make.
   */
  function healthWord(value: string): string {
    const key = HEALTH_LABEL_KEY[value as ProjectHealth];
    return key ? tProjects(key) : value;
  }

  // Translate status enum values (e.g. "IN_PROGRESS" → "In behandeling") before interpolation
  const i18nParams = notification.metadata?.i18n
    ? {
        ...notification.metadata.i18n,
        ...(notification.metadata.i18n.oldStatus && {
          oldStatus: tIssues(
            `status.${notification.metadata.i18n.oldStatus}`,
            notification.metadata.i18n.oldStatus,
          ),
        }),
        ...(notification.metadata.i18n.newStatus && {
          newStatus: tIssues(
            `status.${notification.metadata.i18n.newStatus}`,
            notification.metadata.i18n.newStatus,
          ),
        }),
        ...(notification.metadata.i18n.from && {
          from: healthWord(notification.metadata.i18n.from),
        }),
        ...(notification.metadata.i18n.to && {
          to: healthWord(notification.metadata.i18n.to),
        }),
      }
    : notification.metadata?.i18n;

  /**
   * Get translated notification title
   * Uses i18n params from metadata if available, falls back to stored title
   */
  function getTranslatedTitle(): string {
    if (!i18nParams) {
      return notification.title;
    }

    const key = `notifications.content.${notification.type}.title`;
    const translated = t(key, i18nParams);

    // If translation key doesn't exist, t() returns the key itself
    // In that case, fall back to the stored English title
    if (translated === key) {
      return notification.title;
    }

    return translated;
  }

  /**
   * Get translated notification message
   * Uses i18n params from metadata if available, falls back to stored message
   */
  function getTranslatedMessage(): string {
    if (!i18nParams) {
      return notification.message;
    }

    const key = `notifications.content.${notification.type}.message`;
    const translated = t(key, i18nParams);

    // If translation key doesn't exist, fall back to stored message
    if (translated === key) {
      return notification.message;
    }

    return translated;
  }

  // Format relative time with i18n
  function formatRelativeTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('notifications.time.justNow');
    if (diffMins < 60) return t('notifications.time.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('notifications.time.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('notifications.time.daysAgo', { count: diffDays });

    return date.toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
    });
  }

  return (
    <div
      className={cn(
        'relative flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-neutral-50 dark:hover:bg-neutral-800',
        !notification.isRead && 'bg-blue-50/50 dark:bg-blue-900/20',
      )}
      onClick={onClick}
    >
      {/* Unread indicator */}
      {!notification.isRead && (
        <div className="absolute left-1.5 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-primary" />
      )}

      {/* Icon */}
      <div className={cn('flex-shrink-0 mt-0.5', icon.color)}>
        <Icon name={icon.name} className="h-5 w-5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-sm text-neutral-900 dark:text-neutral-100 line-clamp-2',
            !notification.isRead && 'font-medium',
          )}
        >
          {getTranslatedTitle()}
        </p>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 line-clamp-2">
          {getTranslatedMessage()}
        </p>
        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
          {formatRelativeTime(notification.createdAt)}
        </p>
      </div>

      {/* Dismiss button */}
      <Button
        variant="ghost"
        size="icon"
        className="flex-shrink-0 h-7 w-7 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 dark:text-neutral-500 dark:hover:text-neutral-300 dark:hover:bg-neutral-700"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
      >
        <Icon name="close" className="h-4 w-4" />
      </Button>
    </div>
  );
}
